<template>
  <card card-body-classes="table-full-width" no-footer-line>
    <h4 slot="header" class="card-title">
      {{ $t("message.creditNotes") }}
    </h4>
    <div class="card-body">
      <b-form class="d-flex mb-2" inline @submit.prevent>
        <b-form-select
          v-model="pagination.perPage"
          aria-controls="creditNotes__table"
          :options="pagination.perPageOptions"
        >
        </b-form-select>
        <fg-input
          id="creditNotes__searchInput"
          class="ml-2"
          v-model="searchQuery"
          placeholder="Search..."
          aria-label="Search"
          aria-controls="invoices__table"
        ></fg-input>
      </b-form>
      <b-table
        id="creditNotes__table"
        :items="creditNotes"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :filter="searchQuery"
        :per-page="pagination.perPage"
        :current-page="pagination.currentPage"
        :busy="isLoading"
        :show-empty="!isLoading && !creditNotes.length"
        stacked="md"
        striped
        @filtered="filteredItems => (searchItemsLength = filteredItems.length)"
      >
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
          </div>
        </template>
        <template v-slot:empty>
          <div v-if="error" class="text-center text-danger my-2">
            <p>{{ $t("message.somethingWentWrong") }}</p>
            <p>{{ error }}</p>
          </div>
          <div v-else class="text-center text-primary my-2">
            <p>{{ $t("message.noCreditNotes") }}</p>
          </div>
        </template>
        <template v-slot:cell(number)="data">
          <router-link tag="a" :to="`/credit-notes/view/${data.item._id}`">{{
            data.item.number
          }}</router-link>
        </template>
        <template v-slot:cell(invoiceNumber)="data">
          <router-link tag="a" :to="`/invoices/view/${data.item.invoiceId}`">{{
            data.item.invoiceNumber
          }}</router-link>
        </template>
        <template v-slot:cell(to)="data">
          <router-link tag="a" :to="`/customers/view/${data.item.to._id}`">{{
            data.item.to.companyName
          }}</router-link>
        </template>
        <template v-slot:cell(actions)="data">
          <drop-down
            class="row-actions"
            tag="button"
            position="right"
            icon="fa fa-ellipsis-v"
            hideArrow
            v-if="$can('update', 'CREDIT_NOTES')"
          >
            <router-link
              tag="a"
              class="dropdown-item"
              :to="`/credit-notes/view/${data.item._id}`"
              >{{ $t("message.view") }}</router-link
            >
            <button class="dropdown-item" @click="handleDownloadPdf(data.item)">
              {{ $t("message.downloadPdf") }}
            </button>
          </drop-down>
        </template>
      </b-table>
      <div class="d-flex justify-content-between">
        <div>
          <span class="card-category"
            >Showing {{ from + 1 }} to {{ to }} of {{ total }} entries</span
          >
        </div>
        <n-pagination
          class="pagination-no-border"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="total"
        />
      </div>
    </div>
  </card>
</template>
<script>
import { mapActions, mapGetters } from "vuex";
import _ from "lodash";
import { Pagination as NPagination } from "src/components";
import { dateToHuman } from "@/helpers/helpers";
import { downloadCreditNotePdf } from "@/helpers/documents";

export default {
  components: {
    NPagination
  },
  data() {
    return {
      loader: null,
      fields: [
        {
          key: "number",
          label: this.$t("message.dateSent"),
          formatter: dateToHuman,
          sortable: true
        },
        {
          key: "invoiceNumber",
          label: this.$t("message.creditNoteCorrespondingInvoice"),
          sortable: true
        },
        {
          key: "to",
          label: this.$t("message.creditNoteCreditedTo"),
          sortable: true
        },
        {
          key: "total",
          label: this.$t("message.creditNoteTotal"),
          formatter: value => (value ? value.displayValue : ""),
          sortable: true
        },
        {
          key: "reason",
          label: this.$t("message.creditNoteReason"),
          // TODO - change formatter to an ENUM once set of Reasons confirmed
          formatter: value => _.startCase(_.toLower(value)),
          sortable: true
        },
        {
          key: "actions",
          label: this.$t("message.actions"),
          sortable: false
        }
      ],
      searchQuery: null,
      searchItemsLength: 0,
      sortBy: "number",
      sortDesc: true,
      pagination: {
        perPage: 5,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50]
      },
      isLoading: false,
      error: null
    };
  },
  methods: {
    ...mapActions(["fetchCreditNotes"]),
    async handleDownloadPdf(creditNote) {
      this.loader = this.$loading.show();

      try {
        await downloadCreditNotePdf(creditNote);
      } catch (error) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: this.$t("message.createPdfFailure")
        });
      } finally {
        this.loader.hide();
      }
    },
    showLoadingIndicator() {
      this.isLoading = true;
    },
    hideLoadingIndicator() {
      this.isLoading = false;
    },
    showError(error) {
      this.error = error;
    }
  },
  computed: {
    ...mapGetters(["creditNotes"]),
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    total() {
      return this.searchQuery
        ? this.searchItemsLength
        : this.creditNotes.length;
    },
    to() {
      let highBound = this.from + this.pagination.perPage;
      if (this.total < highBound) {
        highBound = this.total;
      }
      return highBound;
    }
  },
  async created() {
    try {
      this.showLoadingIndicator();
      await this.fetchCreditNotes();
    } catch (e) {
      this.showError(e.message);
    } finally {
      this.hideLoadingIndicator();
    }
  }
};
</script>
